import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import openHtmlInNewTab from '@/lib/openHtmlInNewTab';
// Phase 185.e — popup-blocker-proof signed view links
import openSignedDocInNewTab from '@/lib/openSignedDocInNewTab';
import { downloadDocument } from '@/lib/downloadDocument';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Briefcase, IndianRupee, CalendarDays, CheckCircle, XCircle, Clock,
  User, Award, Flame, Lock, Loader2, Camera, FileText, ClipboardCheck, Star,
  Users, Trophy, Plus, Eye, MessageSquare, Download, QrCode, Sparkles, MapPin,
  AlertTriangle,
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { getSportMatrix, AVAILABLE_SPORTS_LIST } from '@/lib/sportsMetrics';
import { toast } from 'sonner';
import { resolvePhotoUrl, bestPhotoFor } from '@/lib/photo';

export default function EmployeePortal() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ leave_type: 'CL', start_date: '', end_date: '', reason: '' });
  const [showResetPwd, setShowResetPwd] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: '', new_password: '', confirm: '' });
  const [savingPwd, setSavingPwd] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [attMonth, setAttMonth] = useState(new Date().toISOString().slice(0, 7));
  // Coach data
  const [students, setStudents] = useState([]);
  const [events, setEvents] = useState([]);
  const [attDate, setAttDate] = useState(new Date().toISOString().split('T')[0]);
  const [coachAttRecords, setCoachAttRecords] = useState({});
  const [progressCards, setProgressCards] = useState([]);
  const [showAddProgress, setShowAddProgress] = useState(null);
  const [progressForm, setProgressForm] = useState({ skills_rating: {}, comments: '', sport_override: '' });

  const [cockpit, setCockpit] = useState(null);
  const [reviews, setReviews] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [profRes, payRes, leaveRes, attRes, cockRes, revRes] = await Promise.all([
        api.get('/employee/me').catch(() => null),
        api.get('/employee/payslips').catch(() => ({ data: [] })),
        api.get('/employee/leaves').catch(() => ({ data: [] })),
        api.get('/employee/attendance').catch(() => ({ data: { records: [], stats: {} } })),
        api.get('/employees/me/cockpit').catch(() => null),
        api.get('/performance-reviews').catch(() => ({ data: [] })),
      ]);
      if (profRes) {
        setProfile(profRes.data);
        setProfilePhoto(bestPhotoFor(profRes.data));
      }
      setPayslips(payRes?.data || []);
      setLeaves(leaveRes?.data || []);
      setAttendance(attRes?.data || { records: [], stats: {} });
      if (cockRes) setCockpit(cockRes.data);
      setReviews(revRes?.data || []);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Coach data fetching
  useEffect(() => {
    if (user?.role === 'employee') {
      api.get('/students').then(r => setStudents(r.data || [])).catch(() => {});
      api.get('/events').then(r => setEvents(r.data || [])).catch(() => {});
      api.get('/progress-cards').then(r => setProgressCards(r.data || [])).catch(() => {});
    }
  }, [user]);

  const fetchAttForDate = useCallback(async () => {
    if (!attDate) return;
    try {
      const res = await api.get('/attendance', { params: { date: attDate } });
      const map = {};
      (res.data || []).forEach(r => { map[r.student_id] = r.status; });
      setAttRecords(map);
    } catch {}
  }, [attDate]);
  useEffect(() => { fetchAttForDate(); }, [fetchAttForDate]);

  const handleMarkAtt = async (studentId, status) => {
    try {
      await api.post('/attendance', { student_id: studentId, date: attDate, status });
      setAttRecords(prev => ({ ...prev, [studentId]: status }));
      toast.success(`Marked ${status}`);
    } catch { toast.error('Failed'); }
  };

  const handleAddProgress = async (e) => {
    e.preventDefault();
    if (!showAddProgress) return;
    try {
      await api.post('/progress-cards', { student_id: showAddProgress.id, skills_rating: progressForm.skills_rating, comments: progressForm.comments });
      toast.success('Progress card added');
      setShowAddProgress(null);
      setProgressForm({ skills_rating: {}, comments: '', sport_override: '' });
      api.get('/progress-cards').then(r => setProgressCards(r.data || [])).catch(() => {});
    } catch { toast.error('Failed'); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error('Max 4 MB'); return; }
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) { toast.error('Use JPG, PNG, or WEBP'); return; }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/employees/me/profile-photo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProfilePhoto(resolvePhotoUrl(res.data.photo_url));
      // Refresh local AuthContext user so sidebar avatar updates immediately
      try {
        const stored = JSON.parse(localStorage.getItem('outplay_user') || '{}');
        stored.profile_photo_url = res.data.photo_url;
        localStorage.setItem('outplay_user', JSON.stringify(stored));
        window.dispatchEvent(new CustomEvent('outplay:profile-photo-updated', { detail: { url: res.data.photo_url } }));
      } catch (e) { /* non-fatal */ }
      toast.success('Profile photo updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const [downloadingIdCard, setDownloadingIdCard] = useState(false);
  const handleDownloadIdCard = async () => {
    if (!profile?.id) return;
    setDownloadingIdCard(true);
    try {
      const res = await api.get(`/employees/${profile.id}/id-card`);
      const a = document.createElement('a');
      a.href = res.data.image_base64;
      a.download = res.data.filename || 'staff-id-card.png';
      a.click();
      toast.success('ID card downloaded');
    } catch (err) {
      toast.error('Could not generate ID card');
    } finally {
      setDownloadingIdCard(false);
    }
  };

  const handleApplyLeave = async (e) => {
    e.preventDefault();
    try { await api.post('/leaves', leaveForm); toast.success('Leave applied'); setShowLeaveDialog(false); setLeaveForm({ leave_type: 'CL', start_date: '', end_date: '', reason: '' }); fetchData(); } catch { toast.error('Failed'); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (pwdForm.new_password.length < 6) { toast.error('Min 6 characters'); return; }
    if (pwdForm.new_password !== pwdForm.confirm) { toast.error('Passwords do not match'); return; }
    setSavingPwd(true);
    try { await api.post('/auth/change-password', { current_password: pwdForm.current, new_password: pwdForm.new_password }); toast.success('Password changed'); setShowResetPwd(false); setPwdForm({ current: '', new_password: '', confirm: '' }); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSavingPwd(false); }
  };

  if (loading) return <div className="space-y-6"><Skeleton className="h-14 w-64" /><div className="grid grid-cols-4 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div><Skeleton className="h-64 rounded-xl" /></div>;

  const p = profile || {};
  const name = p.name || user?.name || 'Employee';
  const salary = p.base_salary || 0;
  const pendingLeaves = leaves.filter(l => l.status === 'pending').length;
  const approvedLeaves = leaves.filter(l => l.status === 'approved').length;
  const attStats = attendance?.stats || {};
  const attRecords = attendance?.records || [];
  const monthRecords = attRecords.filter(r => r.date?.startsWith(attMonth));

  // Calendar
  const calendarDays = (() => {
    const [y, m] = attMonth.split('-').map(Number);
    const firstDay = new Date(y, m - 1, 1).getDay();
    const daysInMonth = new Date(y, m, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push({ day: d, dateStr: `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}` });
    return days;
  })();
  const mPresent = monthRecords.filter(r => r.status === 'present').length;
  const mAbsent = monthRecords.filter(r => r.status === 'absent').length;
  const mRate = monthRecords.length > 0 ? Math.round(mPresent / monthRecords.length * 100) : (attStats.rate || 0);

  return (
    <div className="space-y-5" data-testid="employee-portal">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="relative group">
          {profilePhoto ? <img src={profilePhoto} alt={name} className="h-14 w-14 rounded-full object-cover shadow-md border-2 border-border" /> : <div className="h-14 w-14 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center shadow-md text-slate-600 dark:text-slate-300 text-xl font-bold">{name[0]}</div>}
          <label className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity cursor-pointer"><Camera className="h-4 w-4 text-white" /><input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} /></label>
          {uploadingPhoto && <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center"><Loader2 className="h-4 w-4 text-white animate-spin" /></div>}
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-display font-bold text-foreground">{name}</h1>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <Badge variant="outline" className="text-xs font-mono">{p.custom_id || ''}</Badge>
            <Badge className="text-xs bg-blue-50 text-blue-700 border-blue-200">{p.role || 'Employee'}</Badge>
            <Badge className="text-xs bg-slate-50 text-slate-600 border-slate-200">{p.department || ''}</Badge>
          </div>
        </div>
      </div>

      {/* KPI Cards - Subtle */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border"><CardContent className="p-4">
          <div className="flex items-center justify-between mb-2"><span className="text-xs text-muted-foreground">Monthly Salary</span><IndianRupee className="h-4 w-4 text-blue-500" /></div>
          <p className="text-xl font-display font-bold tabular-nums">₹{salary.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">{payslips.length} payslip{payslips.length !== 1 ? 's' : ''}</p>
        </CardContent></Card>
        <Card className="border"><CardContent className="p-4">
          <div className="flex items-center justify-between mb-2"><span className="text-xs text-muted-foreground">Attendance</span><ClipboardCheck className="h-4 w-4 text-emerald-500" /></div>
          <p className="text-xl font-display font-bold tabular-nums">{attStats.rate || 0}%</p>
          <p className="text-xs text-muted-foreground">{attStats.present || 0} days present</p>
        </CardContent></Card>
        <Card className="border"><CardContent className="p-4">
          <div className="flex items-center justify-between mb-2"><span className="text-xs text-muted-foreground">Leaves</span><CalendarDays className="h-4 w-4 text-amber-500" /></div>
          <p className="text-xl font-display font-bold tabular-nums">{approvedLeaves}</p>
          <p className="text-xs text-muted-foreground">{pendingLeaves} pending</p>
        </CardContent></Card>
        <Card className="border"><CardContent className="p-4">
          <div className="flex items-center justify-between mb-2"><span className="text-xs text-muted-foreground">Status</span><CheckCircle className="h-4 w-4 text-emerald-500" /></div>
          <p className="text-xl font-display font-bold capitalize">{p.status || 'Active'}</p>
          <p className="text-xs text-muted-foreground">Since {p.join_date || '-'}</p>
        </CardContent></Card>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2">
        <Dialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
          <DialogTrigger asChild><Button size="sm"><CalendarDays className="h-4 w-4 mr-1" /> Apply Leave</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>Apply Leave</DialogTitle></DialogHeader>
            <form onSubmit={handleApplyLeave} className="space-y-3">
              <div className="space-y-1.5"><Label className="text-xs">Type</Label><Select value={leaveForm.leave_type} onValueChange={v => setLeaveForm({...leaveForm, leave_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="CL">Casual</SelectItem><SelectItem value="SL">Sick</SelectItem><SelectItem value="PL">Paid</SelectItem><SelectItem value="LOP">LOP</SelectItem></SelectContent></Select></div>
              <div className="grid grid-cols-2 gap-3"><div className="space-y-1.5"><Label className="text-xs">From</Label><Input type="date" value={leaveForm.start_date} onChange={e => setLeaveForm({...leaveForm, start_date: e.target.value})} required /></div><div className="space-y-1.5"><Label className="text-xs">To</Label><Input type="date" value={leaveForm.end_date} onChange={e => setLeaveForm({...leaveForm, end_date: e.target.value})} required /></div></div>
              <div className="space-y-1.5"><Label className="text-xs">Reason</Label><Input value={leaveForm.reason} onChange={e => setLeaveForm({...leaveForm, reason: e.target.value})} /></div>
              <Button type="submit" className="w-full">Apply</Button>
            </form>
          </DialogContent>
        </Dialog>
        <Button size="sm" variant="outline" onClick={() => setShowResetPwd(true)}><Lock className="h-4 w-4 mr-1" /> Change Password</Button>
        <Button size="sm" variant="outline" onClick={handleDownloadIdCard} disabled={downloadingIdCard || !profile?.id} data-testid="download-id-card">
          {downloadingIdCard ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <QrCode className="h-4 w-4 mr-1" />} Download ID Card
        </Button>
      </div>

      {/* Phase 79 — Personal Cockpit (visible to coaches/employees with linked profile) */}
      {cockpit && (
        <div className="grid md:grid-cols-3 gap-3" data-testid="employee-cockpit">
          {/* My Schedule */}
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <CalendarDays className="h-4 w-4 text-blue-600" />
                <h3 className="text-sm font-semibold">My Upcoming Schedule</h3>
                <Badge variant="outline" className="text-[10px] ml-auto">{cockpit.upcoming_sessions?.length || 0}</Badge>
              </div>
              {(cockpit.upcoming_sessions || []).length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">No upcoming sessions assigned</p>
              ) : (
                <div className="space-y-2">
                  {cockpit.upcoming_sessions.slice(0, 3).map(s => (
                    <div key={s.id} className="text-xs border-l-2 border-blue-200 pl-2 py-1" data-testid={`cockpit-session-${s.id}`}>
                      <p className="font-medium truncate">{s.title}</p>
                      <p className="text-muted-foreground">{s.date}{s.time ? ` · ${s.time}` : ''}{s.batch ? ` · ${s.batch}` : ''}</p>
                      {s.location && <p className="text-muted-foreground flex items-center gap-1"><MapPin className="h-2.5 w-2.5" /> {s.location}</p>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* My Attendance */}
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <ClipboardCheck className="h-4 w-4 text-emerald-600" />
                <h3 className="text-sm font-semibold">This Month</h3>
              </div>
              <p className="text-3xl font-display font-bold tabular-nums text-emerald-700">{cockpit.attendance_this_month?.attendance_pct || 0}%</p>
              <div className="grid grid-cols-3 gap-1 mt-2 text-[10px]">
                <div className="text-center"><p className="font-bold tabular-nums">{cockpit.attendance_this_month?.present || 0}</p><p className="text-muted-foreground">Present</p></div>
                <div className="text-center"><p className="font-bold tabular-nums">{cockpit.attendance_this_month?.half_day || 0}</p><p className="text-muted-foreground">Half-day</p></div>
                <div className="text-center"><p className="font-bold tabular-nums">{cockpit.attendance_this_month?.absent || 0}</p><p className="text-muted-foreground">Absent</p></div>
              </div>
            </CardContent>
          </Card>

          {/* My Latest Payslip */}
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <IndianRupee className="h-4 w-4 text-amber-600" />
                <h3 className="text-sm font-semibold">Latest Payslip</h3>
              </div>
              {(cockpit.latest_payslips || []).length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">No payslips yet</p>
              ) : (() => {
                const p0 = cockpit.latest_payslips[0];
                return (
                  <>
                    <p className="text-2xl font-display font-bold tabular-nums">₹{(p0.net_salary || 0).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">{p0.month}/{p0.year} · <span className="capitalize">{p0.status}</span></p>
                    {(p0.bonus || 0) > 0 && (
                      <p className="text-[11px] text-emerald-700 mt-1">+ ₹{p0.bonus.toLocaleString()} bonus</p>
                    )}
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Phase 79 — Tabs (Profile / Attendance / Payslips / Leaves / Reviews) */}
      <Tabs defaultValue="profile">
        <TabsList className="w-full flex flex-wrap h-auto gap-1.5 bg-transparent p-0">
          <TabsTrigger value="profile" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white bg-blue-50 text-blue-700 text-xs rounded-lg px-4"><User className="h-3.5 w-3.5 mr-1" /> My Profile</TabsTrigger>
          <TabsTrigger value="attendance" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white bg-emerald-50 text-emerald-700 text-xs rounded-lg px-4"><CalendarDays className="h-3.5 w-3.5 mr-1" /> My Attendance</TabsTrigger>
          <TabsTrigger value="payslips" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white bg-amber-50 text-amber-700 text-xs rounded-lg px-4"><IndianRupee className="h-3.5 w-3.5 mr-1" /> Payslips</TabsTrigger>
          <TabsTrigger value="leaves" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white bg-purple-50 text-purple-700 text-xs rounded-lg px-4"><CalendarDays className="h-3.5 w-3.5 mr-1" /> Leaves</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews" className="data-[state=active]:bg-rose-600 data-[state=active]:text-white bg-rose-50 text-rose-700 text-xs rounded-lg px-4"><Star className="h-3.5 w-3.5 mr-1" /> Reviews</TabsTrigger>
        </TabsList>

        {/* Phase 79 — My Performance Reviews */}
        <TabsContent value="reviews">
          <Card><CardContent className="p-0">
            <div className="p-3 border-b flex items-center justify-between">
              <h3 className="text-sm font-semibold flex items-center gap-2"><Star className="h-4 w-4 text-rose-600" /> My Performance Reviews</h3>
              <Badge variant="outline" className="text-[10px]">{reviews.length}</Badge>
            </div>
            {reviews.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground text-sm">No performance reviews yet. Your academy owner will publish quarterly reviews here.</div>
            ) : (
              <div className="divide-y" data-testid="reviews-list">
                {reviews.map(r => (
                  <div key={r.id} className="p-4" data-testid={`review-${r.id}`}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <p className="text-sm font-semibold">Q{r.quarter} {r.year} · Overall <span className={`tabular-nums ${r.overall_score >= 8 ? 'text-emerald-700' : r.overall_score >= 6 ? 'text-amber-700' : 'text-red-700'}`}>{r.overall_score}/10</span></p>
                        <p className="text-[11px] text-muted-foreground">Reviewer: {r.reviewer_name || '—'} · {new Date(r.updated_at).toLocaleDateString()}</p>
                      </div>
                      <Badge className={`text-[10px] ${r.overall_score >= 8 ? 'bg-emerald-100 text-emerald-700' : r.overall_score >= 6 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'} border-0`}>
                        {r.overall_score >= 8 ? 'Excellent' : r.overall_score >= 6 ? 'Good' : 'Needs Focus'}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div className="bg-muted/40 rounded p-2">
                        <p className="text-[10px] uppercase text-muted-foreground">Player Growth</p>
                        <p className="text-base font-bold tabular-nums">{r.player_growth_score}/10</p>
                      </div>
                      <div className="bg-muted/40 rounded p-2">
                        <p className="text-[10px] uppercase text-muted-foreground">Training Consistency</p>
                        <p className="text-base font-bold tabular-nums">{r.training_consistency_score}/10</p>
                      </div>
                    </div>
                    {r.strengths && <p className="text-xs"><span className="font-semibold text-emerald-700">Strengths:</span> {r.strengths}</p>}
                    {r.improvement_areas && <p className="text-xs mt-1"><span className="font-semibold text-amber-700">Improvement:</span> {r.improvement_areas}</p>}
                    {r.notes && <p className="text-xs mt-1 text-muted-foreground italic">{r.notes}</p>}
                  </div>
                ))}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* Students Tab */}
        <TabsContent value="students">
          <Card><CardContent className="p-0">
            <div className="p-3 border-b"><h3 className="text-sm font-semibold">My Students ({students.filter(s=>s.status==='active').length})</h3></div>
            <Table><TableHeader><TableRow><TableHead>Student</TableHead><TableHead className="hidden md:table-cell">Sport</TableHead><TableHead className="hidden md:table-cell">Batch</TableHead><TableHead>Phone</TableHead><TableHead>Status</TableHead><TableHead className="w-20"></TableHead></TableRow></TableHeader><TableBody>
              {students.filter(s=>s.status==='active').length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No students assigned</TableCell></TableRow>
              : students.filter(s=>s.status==='active').map(s => (
                <TableRow key={s.id}>
                  <TableCell><div className="flex items-center gap-2"><div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center"><span className="text-xs font-bold text-blue-600">{(s.name||'?')[0]}</span></div><div><p className="text-sm font-medium">{s.name}</p><p className="text-xs text-muted-foreground">{s.email}</p></div></div></TableCell>
                  <TableCell className="hidden md:table-cell"><Badge variant="outline" className="text-xs">{s.sport||'-'}</Badge></TableCell>
                  <TableCell className="hidden md:table-cell text-sm">{s.batch||'-'}</TableCell>
                  <TableCell className="text-sm">{s.phone||'-'}</TableCell>
                  <TableCell><Badge className="text-xs bg-emerald-100 text-emerald-700 border-0">{s.status}</Badge></TableCell>
                  <TableCell><Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => toast.info(`${s.name} profile`)}><Eye className="h-3 w-3 mr-1" /> View</Button></TableCell>
                </TableRow>
              ))}
            </TableBody></Table>
          </CardContent></Card>
        </TabsContent>

        {/* Mark Attendance Tab */}
        <TabsContent value="mark-attendance">
          <Card><CardContent className="p-4">
            <div className="flex items-center justify-between mb-4"><h3 className="text-sm font-semibold">Mark Attendance</h3>
              <div className="flex items-center gap-2"><Input type="date" className="h-8 text-xs w-40" value={attDate} onChange={e => setAttDate(e.target.value)} />
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={async () => { const active = students.filter(s=>s.status==='active'); for (const s of active) { await api.post('/attendance', { student_id: s.id, date: attDate, status: 'present' }).catch(()=>{}); } setAttRecords(Object.fromEntries(active.map(s=>[s.id,'present']))); toast.success(`All ${active.length} marked present`); }}><CheckCircle className="h-3 w-3 mr-1" /> All Present</Button>
              </div>
            </div>
            <div className="space-y-1">{students.filter(s=>s.status==='active').map(s => (
              <div key={s.id} className={`flex items-center justify-between p-2 rounded-lg ${attRecords[s.id]==='present'?'bg-emerald-50':attRecords[s.id]==='absent'?'bg-red-50':'bg-muted/30'}`}>
                <div className="flex items-center gap-2"><div className="h-7 w-7 rounded-full bg-blue-100 flex items-center justify-center"><span className="text-[10px] font-bold text-blue-600">{(s.name||'?')[0]}</span></div><span className="text-sm font-medium">{s.name}</span><span className="text-xs text-muted-foreground">{s.batch}</span></div>
                <div className="flex gap-1">
                  <Button size="sm" variant={attRecords[s.id]==='present'?'default':'outline'} className="h-7 text-xs" onClick={()=>handleMarkAtt(s.id,'present')}><CheckCircle className="h-3 w-3 mr-0.5" /> P</Button>
                  <Button size="sm" variant={attRecords[s.id]==='absent'?'destructive':'outline'} className="h-7 text-xs" onClick={()=>handleMarkAtt(s.id,'absent')}><XCircle className="h-3 w-3 mr-0.5" /> A</Button>
                  <Button size="sm" variant={attRecords[s.id]==='late'?'secondary':'outline'} className="h-7 text-xs" onClick={()=>handleMarkAtt(s.id,'late')}><Clock className="h-3 w-3 mr-0.5" /> L</Button>
                </div>
              </div>
            ))}</div>
          </CardContent></Card>
        </TabsContent>

        {/* Progress Tab */}
        <TabsContent value="progress">
          <div className="space-y-4">
            <div className="flex justify-end"><Button size="sm" onClick={() => { if (students.length > 0) setShowAddProgress(students[0]); }}><Plus className="h-4 w-4 mr-1" /> Add Progress Card</Button></div>
            <Card><CardContent className="p-0">
              <Table><TableHeader><TableRow><TableHead>Student</TableHead><TableHead>Coach</TableHead><TableHead>Skills</TableHead><TableHead className="hidden md:table-cell">Comments</TableHead><TableHead>Date</TableHead></TableRow></TableHeader><TableBody>
                {progressCards.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground">No progress cards yet</TableCell></TableRow>
                : progressCards.map(c => (
                  <TableRow key={c.id}>
                    <TableCell className="text-sm font-medium">{c.student_name||'-'}</TableCell>
                    <TableCell className="text-sm">{c.coach_name||'-'}</TableCell>
                    <TableCell>{c.skills_rating && Object.entries(c.skills_rating).map(([k,v])=><Badge key={k} variant="outline" className="text-[9px] mr-1 capitalize">{k}: {v}</Badge>)}</TableCell>
                    <TableCell className="hidden md:table-cell text-xs text-muted-foreground max-w-[150px] truncate">{c.comments||'-'}</TableCell>
                    <TableCell className="text-xs">{c.created_at?.slice(0,10)}</TableCell>
                  </TableRow>
                ))}
              </TableBody></Table>
            </CardContent></Card>
          </div>
        </TabsContent>

        {/* Events Tab */}
        <TabsContent value="events">
          <div className="grid md:grid-cols-2 gap-4">
            {events.length === 0 ? <Card className="md:col-span-2 border-dashed"><CardContent className="flex flex-col items-center justify-center py-16"><Trophy className="h-12 w-12 text-muted-foreground/40 mb-4" /><h3 className="text-lg font-semibold mb-1">No events</h3></CardContent></Card>
            : events.map(ev => (
              <Card key={ev.id}><CardContent className="p-4">
                <div className="flex items-start justify-between mb-2"><div><Badge className={`text-xs mb-1 border-0 ${ev.status==='upcoming'?'bg-blue-100 text-blue-700':'bg-emerald-100 text-emerald-700'}`}>{ev.status}</Badge><h3 className="text-sm font-semibold">{ev.title}</h3></div></div>
                {ev.description && <p className="text-xs text-muted-foreground mb-2">{ev.description}</p>}
                <div className="text-xs text-muted-foreground space-y-1"><p>{ev.date}{ev.end_date && ev.end_date !== ev.date ? ` — ${ev.end_date}` : ''}</p>{ev.venue && <p>{ev.venue}</p>}<p>{ev.participant_count||0}{ev.max_participants ? `/${ev.max_participants}` : ''} participants • ₹{ev.entry_fee||0}</p></div>
              </CardContent></Card>
            ))}
          </div>
        </TabsContent>

        {/* Profile */}
        <TabsContent value="profile">
          <Card className="overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-7 text-white">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
                <div className="relative group flex-shrink-0">
                  {profilePhoto ? <img src={profilePhoto} alt={name} className="h-24 w-24 rounded-full object-cover border-4 border-white/30 shadow-xl" /> : <div className="h-24 w-24 rounded-full bg-white/20 flex items-center justify-center border-4 border-white/30 shadow-xl text-4xl font-bold">{name[0]}</div>}
                  <label className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity cursor-pointer"><Camera className="h-6 w-6 text-white" /><input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} /></label>
                </div>
                <div className="text-center sm:text-left flex-1">
                  <h2 className="text-2xl font-display font-bold">{name}</h2>
                  <p className="text-blue-200 text-sm">{p.email || user?.email || ''}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-2 justify-center sm:justify-start">
                    <Badge className="bg-white/20 border-white/30 text-white text-xs font-mono">{p.custom_id||''}</Badge>
                    <Badge className="bg-white/20 border-white/30 text-white text-xs">{p.role||'Employee'}</Badge>
                    <Badge className="bg-white/20 border-white/30 text-white text-xs">{p.department||''}</Badge>
                    <Badge className="bg-emerald-400/30 border-emerald-300/30 text-white text-xs">{p.status||'active'}</Badge>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-medium cursor-pointer border border-white/20"><Camera className="h-3.5 w-3.5" /> Photo<input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} /></label>
                  <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10 text-xs" onClick={() => setShowResetPwd(true)}><Lock className="h-3.5 w-3.5 mr-1" /> Password</Button>
                </div>
              </div>
            </div>
            <CardContent className="p-6">
              <div className="mb-6"><h3 className="text-sm font-bold text-blue-700 uppercase tracking-wider mb-3"><User className="h-4 w-4 inline mr-1" /> Personal Information</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">{[{l:'Full Name',v:name},{l:'Email',v:p.email||user?.email||'-'},{l:'Phone',v:p.phone||'-'},{l:'Designation',v:p.designation||'-'},{l:'Join Date',v:p.join_date||'-'},{l:'Status',v:p.status||'active'}].map(i=>(<div key={i.l} className="py-2 border-b border-border/50"><p className="text-[10px] text-muted-foreground uppercase tracking-wider">{i.l}</p><p className="text-sm font-medium mt-0.5">{i.v}</p></div>))}</div>
              </div>
              <div><h3 className="text-sm font-bold text-indigo-700 uppercase tracking-wider mb-3"><Briefcase className="h-4 w-4 inline mr-1" /> Employment Details</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">{[{l:'Employee ID',v:p.custom_id||'-'},{l:'Role',v:p.role||'-'},{l:'Department',v:p.department||'-'},{l:'Base Salary',v:`₹${salary.toLocaleString()}`}].map(i=>(<div key={i.l} className="py-2 border-b border-border/50"><p className="text-[10px] text-muted-foreground uppercase tracking-wider">{i.l}</p><p className="text-sm font-medium mt-0.5">{i.v}</p></div>))}</div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Attendance - Calendar Style */}
        <TabsContent value="attendance">
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Card><CardContent className="p-5">
                <div className="flex items-center justify-between mb-3"><h3 className="text-sm font-semibold">Monthly Summary</h3>
                  <select className="text-xs border rounded-lg px-2 py-1 bg-background" value={attMonth} onChange={e => setAttMonth(e.target.value)}>
                    {Array.from({length: 12}, (_, i) => { const d = new Date(2026, i); return <option key={i} value={`2026-${String(i+1).padStart(2,'0')}`}>{d.toLocaleString('default',{month:'long'})} 2026</option>; })}
                  </select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-2 rounded-lg bg-emerald-50"><p className="text-xl font-bold text-emerald-600 tabular-nums">{mPresent}</p><p className="text-[10px] text-muted-foreground">Present</p></div>
                  <div className="text-center p-2 rounded-lg bg-red-50"><p className="text-xl font-bold text-red-500 tabular-nums">{mAbsent}</p><p className="text-[10px] text-muted-foreground">Absent</p></div>
                  <div className="text-center p-2 rounded-lg bg-blue-50"><p className="text-xl font-bold text-blue-600 tabular-nums">{mRate}%</p><p className="text-[10px] text-muted-foreground">Rate</p></div>
                </div>
              </CardContent></Card>
              <Card><CardContent className="p-5">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><Award className="h-4 w-4 text-blue-600" /> AI Insights</h3>
                <div className="space-y-2">
                  {mRate >= 90 ? <div className="flex items-start gap-2 p-2 rounded-lg bg-emerald-50"><CheckCircle className="h-3.5 w-3.5 text-emerald-600 mt-0.5" /><p className="text-xs text-emerald-700">Excellent attendance! You're a reliable team member.</p></div>
                  : <div className="flex items-start gap-2 p-2 rounded-lg bg-amber-50"><Clock className="h-3.5 w-3.5 text-amber-600 mt-0.5" /><p className="text-xs text-amber-700">Try to improve your attendance consistency.</p></div>}
                  <div className="flex items-start gap-2 p-2 rounded-lg bg-blue-50"><Star className="h-3.5 w-3.5 text-blue-600 mt-0.5" /><p className="text-xs text-blue-700">Regular attendance leads to better team performance.</p></div>
                </div>
              </CardContent></Card>
            </div>
            <Card><CardContent className="p-5">
              <div className="flex items-center justify-between mb-4"><h3 className="text-sm font-semibold">Calendar</h3>
                <div className="flex items-center gap-3 text-[10px]"><span className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-emerald-500" /> Present</span><span className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-red-400" /> Absent</span><span className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-muted" /> No record</span></div>
              </div>
              <div className="grid grid-cols-7 gap-1.5">
                {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => <div key={d} className="text-center text-[10px] font-semibold text-muted-foreground py-1">{d}</div>)}
                {calendarDays.map((day, i) => {
                  if (!day) return <div key={`e-${i}`} />;
                  const rec = monthRecords.find(r => r.date === day.dateStr);
                  const bg = rec?.status === 'present' ? 'bg-emerald-500 text-white' : rec?.status === 'absent' ? 'bg-red-400 text-white' : 'bg-muted/40 text-muted-foreground';
                  return <div key={day.dateStr} className={`text-center py-1.5 rounded-lg text-xs font-medium ${bg}`} title={`${day.dateStr}: ${rec?.status || 'No record'}`}>{day.day}</div>;
                })}
              </div>
            </CardContent></Card>
          </div>
        </TabsContent>

        {/* Payslips */}
        <TabsContent value="payslips">
          <Card><CardContent className="p-0"><Table><TableHeader><TableRow><TableHead>Period</TableHead><TableHead>Base</TableHead><TableHead className="hidden md:table-cell">Bonus</TableHead><TableHead className="hidden md:table-cell">Deductions</TableHead><TableHead>Net</TableHead><TableHead>Status</TableHead><TableHead className="w-32">Actions</TableHead></TableRow></TableHeader><TableBody>
            {payslips.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No payslips</TableCell></TableRow>
            : payslips.map(ps => (<TableRow key={ps.id}>
              <TableCell className="text-sm">{ps.month}/{ps.year}</TableCell>
              <TableCell className="tabular-nums">₹{(ps.base_salary||0).toLocaleString()}</TableCell>
              <TableCell className="hidden md:table-cell tabular-nums">₹{(ps.bonus||0).toLocaleString()}</TableCell>
              <TableCell className="hidden md:table-cell tabular-nums">₹{(ps.deductions||0).toLocaleString()}</TableCell>
              <TableCell className="font-semibold tabular-nums">₹{(ps.net_salary||0).toLocaleString()}</TableCell>
              <TableCell><Badge className={`text-xs border-0 ${ps.status==='paid'?'bg-emerald-100 text-emerald-700':'bg-amber-100 text-amber-700'}`}>{ps.status}</Badge></TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="View Payslip" onClick={() => openSignedDocInNewTab(
                    `/payslips/${ps.id}/view`,
                    { label: 'Payslip', errorMessage: 'Failed to load payslip' },
                  )}>
                    <Eye className="h-3.5 w-3.5 text-blue-600" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Download PDF" onClick={async () => {
                    await downloadDocument(
                      api,
                      `/payslips/${ps.id}/download`,
                      `Payslip_${ps.month}_${ps.year}.pdf`,
                      { label: 'Payslip' }
                    );
                  }}>
                    <Download className="h-3.5 w-3.5 text-emerald-600" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>))}
          </TableBody></Table></CardContent></Card>
        </TabsContent>

        {/* Leaves - Enhanced Dashboard */}
        <TabsContent value="leaves">
          <div className="space-y-4">
            {/* Leave Balance Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              <Card className="bg-emerald-50/60 dark:bg-emerald-950/10 border-emerald-200/50"><CardContent className="p-4">
                <div className="flex items-center justify-between mb-1"><span className="text-xs text-emerald-600 font-medium">Available</span><CheckCircle className="h-4 w-4 text-emerald-500" /></div>
                <p className="text-2xl font-display font-bold tabular-nums text-emerald-600">{Math.max(0, 20 - approvedLeaves)} days</p>
                <p className="text-xs text-muted-foreground">{Math.round((20 - approvedLeaves) / 20 * 100)}% of 20 days</p>
              </CardContent></Card>
              <Card className="bg-amber-50/60 dark:bg-amber-950/10 border-amber-200/50"><CardContent className="p-4">
                <div className="flex items-center justify-between mb-1"><span className="text-xs text-amber-600 font-medium">Used</span><CalendarDays className="h-4 w-4 text-amber-500" /></div>
                <p className="text-2xl font-display font-bold tabular-nums text-amber-600">{approvedLeaves} days</p>
                <p className="text-xs text-muted-foreground">{leaves.filter(l => l.leave_type === 'SL' && l.status === 'approved').length} sick, {leaves.filter(l => l.leave_type === 'CL' && l.status === 'approved').length} casual</p>
              </CardContent></Card>
              <Card className="bg-red-50/60 dark:bg-red-950/10 border-red-200/50"><CardContent className="p-4">
                <div className="flex items-center justify-between mb-1"><span className="text-xs text-red-600 font-medium">Pending</span><Clock className="h-4 w-4 text-red-500" /></div>
                <p className="text-2xl font-display font-bold tabular-nums text-red-600">{pendingLeaves}</p>
                <p className="text-xs text-muted-foreground">awaiting approval</p>
              </CardContent></Card>
            </div>

            {/* Quick Apply + LoP Info */}
            <div className="grid sm:grid-cols-2 gap-4">
              <Card><CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-3">Quick Apply</h3>
                <form onSubmit={handleApplyLeave} className="space-y-2">
                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-1"><Label className="text-[10px]">Type</Label><Select value={leaveForm.leave_type} onValueChange={v => setLeaveForm({...leaveForm, leave_type: v})}><SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="CL">Casual</SelectItem><SelectItem value="SL">Sick</SelectItem><SelectItem value="PL">Paid</SelectItem><SelectItem value="LOP">Loss of Pay</SelectItem></SelectContent></Select></div>
                    <div className="space-y-1"><Label className="text-[10px]">From</Label><Input type="date" className="h-8 text-xs" value={leaveForm.start_date} onChange={e => setLeaveForm({...leaveForm, start_date: e.target.value})} required /></div>
                    <div className="space-y-1"><Label className="text-[10px]">To</Label><Input type="date" className="h-8 text-xs" value={leaveForm.end_date} onChange={e => setLeaveForm({...leaveForm, end_date: e.target.value})} required /></div>
                  </div>
                  <div className="space-y-1"><Label className="text-[10px]">Reason</Label><Input className="h-8 text-xs" value={leaveForm.reason} onChange={e => setLeaveForm({...leaveForm, reason: e.target.value})} placeholder="Brief reason" /></div>
                  <Button type="submit" size="sm" className="w-full h-8 text-xs">Submit Leave Request</Button>
                </form>
              </CardContent></Card>

              <Card className="bg-blue-50/60 dark:bg-blue-950/10 border-blue-200/50"><CardContent className="p-4">
                <h3 className="text-sm font-semibold text-blue-700 mb-3 flex items-center gap-2"><IndianRupee className="h-4 w-4" /> Loss of Pay Info</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">Daily Rate</span><span className="font-semibold tabular-nums">₹{Math.round(salary / 26).toLocaleString()}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">LoP Days</span><span className="font-semibold tabular-nums">{leaves.filter(l => l.leave_type === 'LOP' && l.status === 'approved').length}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">Deduction</span><span className="font-semibold tabular-nums text-red-600">₹{(leaves.filter(l => l.leave_type === 'LOP' && l.status === 'approved').length * Math.round(salary / 26)).toLocaleString()}</span></div>
                  <Separator />
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">Net Salary</span><span className="font-bold tabular-nums">₹{(salary - leaves.filter(l => l.leave_type === 'LOP' && l.status === 'approved').length * Math.round(salary / 26)).toLocaleString()}</span></div>
                </div>
              </CardContent></Card>
            </div>

            {/* Leave History */}
            <Card><CardContent className="p-0">
              <div className="px-4 py-3 border-b"><h3 className="text-sm font-semibold">Leave History</h3></div>
              <Table><TableHeader><TableRow><TableHead>Type</TableHead><TableHead>From</TableHead><TableHead>To</TableHead><TableHead className="hidden md:table-cell">Days</TableHead><TableHead>Reason</TableHead><TableHead>Status</TableHead></TableRow></TableHeader><TableBody>
                {leaves.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No leave records. Apply your first leave above.</TableCell></TableRow>
                : leaves.map(l => (
                  <TableRow key={l.id}>
                    <TableCell><Badge variant="outline" className={`text-xs ${l.leave_type === 'LOP' ? 'border-red-300 text-red-700' : l.leave_type === 'SL' ? 'border-amber-300 text-amber-700' : ''}`}>{l.leave_type}</Badge></TableCell>
                    <TableCell className="text-sm">{l.start_date}</TableCell>
                    <TableCell className="text-sm">{l.end_date}</TableCell>
                    <TableCell className="hidden md:table-cell tabular-nums">{l.days}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[150px] truncate">{l.reason || '-'}</TableCell>
                    <TableCell><Badge className={`text-xs border-0 ${l.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : l.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{l.status === 'approved' ? <CheckCircle className="h-3 w-3 mr-0.5" /> : l.status === 'rejected' ? <XCircle className="h-3 w-3 mr-0.5" /> : <Clock className="h-3 w-3 mr-0.5" />}{l.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody></Table>
            </CardContent></Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Progress Card Dialog */}
      <Dialog open={!!showAddProgress} onOpenChange={() => setShowAddProgress(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle><Award className="h-5 w-5 inline mr-1 text-orange-600" /> Add Progress Card</DialogTitle></DialogHeader>
          {showAddProgress && (() => {
            // Phase 98.c — Sport-aware metric matrix with manual override
            const resolvedSport = showAddProgress?.sport || showAddProgress?.course || '';
            const activeSport = progressForm.sport_override || resolvedSport;
            const matrix = getSportMatrix(activeSport);
            const isFallback = matrix.isFallback === true;
            return (
            <form onSubmit={handleAddProgress} className="space-y-3">
              <div className="space-y-1.5"><Label className="text-xs">Student</Label>
                <Select value={showAddProgress.id} onValueChange={v => { const s = students.find(st=>st.id===v); if (s) { setShowAddProgress(s); setProgressForm(prev => ({ ...prev, skills_rating: {}, sport_override: '' })); } }}><SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger><SelectContent>{students.filter(s=>s.status==='active').map(s=><SelectItem key={s.id} value={s.id}>{s.name} — {s.batch}</SelectItem>)}</SelectContent></Select>
              </div>
              {isFallback ? (
                <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 flex flex-col gap-2 text-[11px]" data-testid="emp-progress-sport-fallback-banner">
                  <div className="flex items-start gap-1.5 text-amber-800">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-bold">Generic matrix · sport not tagged</p>
                      <p className="text-amber-700 text-[10px]">Pick a sport for specialised metrics.</p>
                    </div>
                  </div>
                  <select
                    value={progressForm.sport_override}
                    onChange={(e) => setProgressForm(prev => ({ ...prev, sport_override: e.target.value, skills_rating: {} }))}
                    className="bg-white border border-amber-300 text-slate-800 px-2 py-1 rounded-md font-bold text-[11px]"
                    data-testid="emp-progress-sport-override-select"
                  >
                    <option value="">Tag Student Sport →</option>
                    {AVAILABLE_SPORTS_LIST.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="rounded-md bg-primary/5 border border-primary/15 px-2.5 py-1.5 text-[11px] text-primary flex items-center justify-between" data-testid="emp-progress-sport-banner">
                  <span><span className="font-semibold">{matrix.sport}</span> matrix</span>
                  {progressForm.sport_override
                    ? <span className="opacity-60">manual override</span>
                    : <span className="opacity-60">from "{resolvedSport}"</span>}
                </div>
              )}
              <div className="space-y-1.5"><Label className="text-xs">Skills (1-5 rating)</Label>
                <div className="grid grid-cols-2 gap-2">
                  {matrix.skills.map(skill => (
                    <div key={skill.id} className="flex items-center gap-2" data-testid={`emp-progress-skill-row-${skill.id}`}>
                      <span className="text-[11px] capitalize w-20 truncate" title={skill.label}>{skill.label}</span>
                      <Input
                        type="number" min="1" max="5" step="0.5"
                        className="h-7 text-xs w-16"
                        value={progressForm.skills_rating[skill.id] || ''}
                        onChange={e => setProgressForm(prev => ({ ...prev, skills_rating: { ...prev.skills_rating, [skill.id]: parseFloat(e.target.value) || 0 } }))}
                        data-testid={`emp-progress-skill-input-${skill.id}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Coach Comments</Label><Textarea className="text-xs" rows={2} value={progressForm.comments} onChange={e => setProgressForm(prev=>({...prev, comments: e.target.value}))} placeholder="Great session today..." /></div>
              <Button type="submit" className="w-full">Save Progress Card</Button>
            </form>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Reset Password */}
      <Dialog open={showResetPwd} onOpenChange={setShowResetPwd}><DialogContent className="max-w-sm"><DialogHeader><DialogTitle><Lock className="h-5 w-5 inline mr-1" /> Change Password</DialogTitle></DialogHeader>
        <form onSubmit={handleResetPassword} className="space-y-3"><div className="space-y-1.5"><Label className="text-xs">Current</Label><Input type="password" value={pwdForm.current} onChange={e => setPwdForm({...pwdForm, current: e.target.value})} required /></div><div className="space-y-1.5"><Label className="text-xs">New</Label><Input type="password" value={pwdForm.new_password} onChange={e => setPwdForm({...pwdForm, new_password: e.target.value})} required /></div><div className="space-y-1.5"><Label className="text-xs">Confirm</Label><Input type="password" value={pwdForm.confirm} onChange={e => setPwdForm({...pwdForm, confirm: e.target.value})} required /></div><Button type="submit" className="w-full" disabled={savingPwd}>{savingPwd ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}{savingPwd ? 'Changing...' : 'Change Password'}</Button></form>
      </DialogContent></Dialog>
    </div>
  );
}
